import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, ChevronLeft, ChevronRight, Play, Pin, Images } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ytThumb, ytEmbed } from "@/lib/youtube";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const U = (id, q) => `https://images.unsplash.com/${id}?${q}&q=85&w=1000`;

const FALLBACK = [
  { kind: "photo", src: "/images/portrait.jpg", caption: "Le Patriarche — François Dossa TANDJIEKPON" },
  {
    kind: "photo",
    src: U("photo-1694286066814-193f5674ba98", "crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxhZnJpY2FuJTIwZmFtaWx5JTIwZ2F0aGVyaW5nJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg3ODE3OTY0fDA&ixlib=rb-4.1.0"),
    caption: "Réunions de famille",
  },
  {
    kind: "photo",
    src: U("photo-1761666519882-59ab0dbe5059", "crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwZmFtaWx5JTIwZ2F0aGVyaW5nJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg3ODE3OTY0fDA&ixlib=rb-4.1.0"),
    caption: "Les grandes tablées",
  },
  {
    kind: "photo",
    src: U("photo-1775135505566-865dcb6f37aa", "crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxhZnJpY2FuJTIwbGFuZHNjYXBlJTIwc3Vuc2V0JTIwcGFsbSUyMHRyZWVzfGVufDB8fHx8MTc4NzgxNzk2NHww&ixlib=rb-4.1.0"),
    caption: "Terres de Banigbé-Gare",
  },
  {
    kind: "photo",
    src: U("photo-1548625149-fc4a29cf7092", "crop=entropy&cs=srgb&fm=jpg"),
    caption: "Église Saint Jean-Baptiste",
  },
  {
    kind: "photo",
    src: U("photo-1601922046210-41e129a3e64a", "crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxjYW5kbGUlMjBmbGFtZSUyMGRhcmt8ZW58MHx8fHwxNzg3ODE3OTY0fDA&ixlib=rb-4.1.0"),
    caption: "Lumière du souvenir",
  },
];

export default function Gallery({ full = false }) {
  const [items, setItems] = useState([]);
  const [themes, setThemes] = useState([]);
  const [theme, setTheme] = useState("Toutes");
  const [open, setOpen] = useState(null);
  const [broken, setBroken] = useState([]);
  const hide = (m) => m.id && setBroken((b) => (b.includes(m.id) ? b : [...b, m.id]));

  useEffect(() => {
    axios.get(`${API}/media`).then((r) => setItems(r.data)).catch(() => {});
    axios.get(`${API}/themes`).then((r) => setThemes(r.data.themes || [])).catch(() => {});
  }, []);

  const source = items.length ? items.filter((m) => !broken.includes(m.id)) : FALLBACK;
  const filtered = theme === "Toutes" ? source : source.filter((m) => m.theme === theme);
  const shown = full ? filtered : filtered.slice(0, 6);
  const srcOf = (m) => (m.path ? `${API}/files/${m.path}` : m.src);

  return (
    <section id="galerie" data-testid="gallery-section" className="px-4 py-16 sm:py-24">
      <div className="mx-auto max-w-5xl">
        <Reveal className="mb-10 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C5A059]">
            Souvenirs
          </p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-stone-900 sm:text-4xl">
            {full ? "Tous les albums" : "Galerie Souvenir"}
          </h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-stone-500 sm:text-base">
            Moments de vie, de famille et de célébration.
          </p>
          {!items.length && (
            <p className="mt-2 text-xs italic text-stone-400">
              Photographies d'illustration — les photos de famille seront bientôt
              numérisées.
            </p>
          )}
          {full && themes.length > 0 && (
            <div data-testid="gallery-themes" className="mt-5 flex flex-wrap justify-center gap-2">
              {["Toutes", ...themes.filter((t) => !items.length || source.some((m) => m.theme === t))].map((t) => (
                <button
                  key={t}
                  data-testid={`gallery-theme-${t}`}
                  onClick={() => setTheme(t)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                    theme === t
                      ? "bg-[#1C1917] text-[#FAF8F5]"
                      : "border border-stone-300 text-stone-500 hover:border-[#C5A059]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </Reveal>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3">
          {shown.map((m, i) => (
            <Reveal key={m.id ?? m.src ?? i} delay={(i % 3) * 0.08}>
              <button
                data-testid={`gallery-image-${i}`}
                onClick={() => setOpen(i)}
                className="group relative block w-full overflow-hidden rounded-2xl"
              >
                {m.kind === "youtube" ? (
                  <>
                    <img
                      src={ytThumb(m.youtube_id)}
                      alt={m.caption || "Vidéo YouTube"}
                      loading="lazy"
                      className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="rounded-full bg-[#C85A32]/90 p-3 text-white transition-transform duration-300 group-hover:scale-110">
                        <Play className="h-5 w-5" />
                      </span>
                    </span>
                  </>
                ) : m.kind === "video" ? (
                  <>
                    <video
                      src={`${srcOf(m)}#t=0.5`}
                      muted
                      playsInline
                      preload="metadata"
                      className="aspect-[4/5] w-full object-cover"
                    />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="rounded-full bg-black/50 p-3 text-white transition-transform duration-300 group-hover:scale-110">
                        <Play className="h-5 w-5" />
                      </span>
                    </span>
                  </>
                ) : (
                  <img
                    src={srcOf(m)}
                    alt={m.caption || "Souvenir"}
                    loading="lazy"
                    onError={() => hide(m)}
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                )}
                {m.pinned && (
                  <span
                    data-testid={`gallery-pinned-${i}`}
                    className="absolute left-2 top-2 rounded-full bg-[#C5A059] p-1.5 text-[#1C1917] shadow"
                  >
                    <Pin className="h-3.5 w-3.5" />
                  </span>
                )}
                {m.theme && (
                  <span className="absolute bottom-2 left-2 rounded-full bg-black/50 px-2.5 py-0.5 text-[10px] font-medium text-white">
                    {m.theme}
                  </span>
                )}
              </button>
            </Reveal>
          ))}
        </div>

        {!full && (
          <Reveal className="mt-8 text-center">
            <a
              data-testid="gallery-view-all"
              href="/galerie"
              className="inline-flex items-center gap-2 rounded-full border border-[#C5A059] px-7 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#8a6b2e] transition-colors duration-300 hover:bg-[#C5A059] hover:text-white"
            >
              <Images className="h-4 w-4" />
              Voir toutes les photos et vidéos
            </a>
          </Reveal>
        )}
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
            onClick={() => setOpen(null)}
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setOpen(null)}
              className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((open + shown.length - 1) % shown.length);
              }}
              className="absolute left-3 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 sm:left-6"
              aria-label="Média précédent"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <motion.figure
              key={open}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              {shown[open].kind === "youtube" ? (
                <div className="aspect-video w-[min(90vw,960px)] overflow-hidden rounded-2xl bg-black">
                  <iframe
                    data-testid="lightbox-youtube"
                    src={ytEmbed(shown[open].youtube_id)}
                    title={shown[open].caption || "Vidéo YouTube"}
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                    className="h-full w-full"
                  />
                </div>
              ) : shown[open].kind === "video" ? (
                <video
                  data-testid="lightbox-video"
                  src={srcOf(shown[open])}
                  controls
                  autoPlay
                  playsInline
                  className="max-h-[75vh] w-auto rounded-2xl"
                />
              ) : (
                <img
                  src={srcOf(shown[open])}
                  alt={shown[open].caption || "Souvenir"}
                  className="max-h-[75vh] w-auto rounded-2xl object-contain"
                />
              )}
              <figcaption
                data-testid="lightbox-caption"
                className="mt-4 text-center font-display text-lg italic text-stone-200"
              >
                {shown[open].caption}
                {shown[open].theme && (
                  <span className="ml-2 text-sm not-italic text-stone-400">
                    • {shown[open].theme}
                  </span>
                )}
              </figcaption>
            </motion.figure>
            <button
              data-testid="lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((open + 1) % shown.length);
              }}
              className="absolute right-3 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 sm:right-6"
              aria-label="Média suivant"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

